import type { OcrLine, OcrResult } from '../types/document'

// Everything in this file runs in the browser. The image never leaves the device.
// The open-source Tesseract engine and language models are served from Samjho's
// own origin (/tesseract/, copied from node_modules at build time) and cached by
// the browser, so no third-party service is contacted.

const TESSERACT_BASE = `${import.meta.env.BASE_URL}tesseract`

export const ACCEPTED_TYPES = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp']
export const MAX_FILE_SIZE = 10 * 1024 * 1024
export const MAX_PDF_PAGES = 3
/** PDFs longer than this are refused outright rather than rendered. */
export const MAX_PDF_TOTAL_PAGES = 60

export type FileProblem = 'type' | 'size'
export function validateFile(file: File): FileProblem | null {
  if (!ACCEPTED_TYPES.includes(file.type)) return 'type'
  if (file.size > MAX_FILE_SIZE) return 'size'
  return null
}

export interface OcrProgress {
  stage: 'download' | 'recognize'
  /** 0-1 within the current stage */
  progress: number
  page?: number
  pages?: number
}

// ── Canvas helpers ────────────────────────────────────────────────────────────

function newCanvas(width: number, height: number): HTMLCanvasElement {
  const canvas = document.createElement('canvas')
  canvas.width = Math.max(1, Math.round(width))
  canvas.height = Math.max(1, Math.round(height))
  return canvas
}

function ctx2d(canvas: HTMLCanvasElement): CanvasRenderingContext2D {
  const ctx = canvas.getContext('2d', { willReadFrequently: true })
  if (!ctx) throw new Error('canvas-unavailable')
  return ctx
}

/** Decodes an image file, applying the camera's EXIF orientation. Caps size to keep phones responsive. */
export async function loadImage(file: File, maxEdge = 3200): Promise<HTMLCanvasElement> {
  const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' })
  const scale = Math.min(1, maxEdge / Math.max(bitmap.width, bitmap.height))
  const canvas = newCanvas(bitmap.width * scale, bitmap.height * scale)
  ctx2d(canvas).drawImage(bitmap, 0, 0, canvas.width, canvas.height)
  bitmap.close()
  return canvas
}

export function rotateCanvas(source: HTMLCanvasElement, quarterTurns: number): HTMLCanvasElement {
  const turns = ((quarterTurns % 4) + 4) % 4
  if (turns === 0) return source
  const swap = turns % 2 === 1
  const canvas = newCanvas(swap ? source.height : source.width, swap ? source.width : source.height)
  const ctx = ctx2d(canvas)
  ctx.translate(canvas.width / 2, canvas.height / 2)
  ctx.rotate((turns * Math.PI) / 2)
  ctx.drawImage(source, -source.width / 2, -source.height / 2)
  return canvas
}

/** Crop rectangle in fractions (0-1) of the image size. */
export interface CropRect { x: number; y: number; w: number; h: number }

export function cropCanvas(source: HTMLCanvasElement, rect: CropRect): HTMLCanvasElement {
  const x = Math.round(rect.x * source.width)
  const y = Math.round(rect.y * source.height)
  const w = Math.round(rect.w * source.width)
  const h = Math.round(rect.h * source.height)
  if (w < 20 || h < 20) return source
  const canvas = newCanvas(w, h)
  ctx2d(canvas).drawImage(source, x, y, w, h, 0, 0, w, h)
  return canvas
}

function luminance(data: Uint8ClampedArray): Float32Array {
  const out = new Float32Array(data.length / 4)
  for (let i = 0, j = 0; i < data.length; i += 4, j += 1) {
    out[j] = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2]
  }
  return out
}

// ── Automatic paper detection ─────────────────────────────────────────────────

/**
 * Finds the sheet of paper in a photo taken on a darker surface (table, floor,
 * lap) so the background doesn't turn into OCR junk. Deliberately conservative:
 * returns null unless a clearly brighter rectangle covers 25-90% of the photo.
 */
export function findPaperBounds(source: HTMLCanvasElement): CropRect | null {
  const scale = Math.min(1, 320 / Math.max(source.width, source.height))
  const canvas = newCanvas(source.width * scale, source.height * scale)
  const ctx = ctx2d(canvas)
  ctx.drawImage(source, 0, 0, canvas.width, canvas.height)
  const { width, height } = canvas
  const gray = luminance(ctx.getImageData(0, 0, width, height).data)

  // Otsu threshold between "paper" and "background"
  const histogram = new Uint32Array(256)
  for (const v of gray) histogram[Math.round(v)] += 1
  let sumAll = 0
  for (let v = 0; v < 256; v += 1) sumAll += v * histogram[v]
  let sumBg = 0
  let weightBg = 0
  let best = 0
  let threshold = 128
  for (let v = 0; v < 256; v += 1) {
    weightBg += histogram[v]
    if (weightBg === 0) continue
    const weightFg = gray.length - weightBg
    if (weightFg === 0) break
    sumBg += v * histogram[v]
    const between = weightBg * weightFg * (sumBg / weightBg - (sumAll - sumBg) / weightFg) ** 2
    if (between > best) {
      best = between
      threshold = v
    }
  }

  const longestRun = (fractions: number[]) => {
    let bestStart = 0
    let bestLength = 0
    let start = -1
    fractions.forEach((f, i) => {
      if (f > 0.4) {
        if (start < 0) start = i
        if (i - start + 1 > bestLength) {
          bestStart = start
          bestLength = i - start + 1
        }
      } else start = -1
    })
    return [bestStart, bestLength] as const
  }
  const rowFractions = Array.from({ length: height }, (_, y) => {
    let bright = 0
    for (let x = 0; x < width; x += 1) if (gray[y * width + x] > threshold) bright += 1
    return bright / width
  })
  const colFractions = Array.from({ length: width }, (_, x) => {
    let bright = 0
    for (let y = 0; y < height; y += 1) if (gray[y * width + x] > threshold) bright += 1
    return bright / height
  })
  const [y0, h] = longestRun(rowFractions)
  const [x0, w] = longestRun(colFractions)
  const area = (w * h) / (width * height)
  if (area < 0.25 || area > 0.9) return null

  // The outside must be clearly darker than the paper, or we are not sure
  let inside = 0
  let insideCount = 0
  let outside = 0
  let outsideCount = 0
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const v = gray[y * width + x]
      if (x >= x0 && x < x0 + w && y >= y0 && y < y0 + h) {
        inside += v
        insideCount += 1
      } else {
        outside += v
        outsideCount += 1
      }
    }
  }
  if (outsideCount === 0 || outside / outsideCount > 0.7 * (inside / insideCount)) return null

  const margin = 0.015
  const x = Math.max(0, x0 / width - margin)
  const y = Math.max(0, y0 / height - margin)
  return { x, y, w: Math.min(1 - x, w / width + 2 * margin), h: Math.min(1 - y, h / height + 2 * margin) }
}

// ── Photo quality check ───────────────────────────────────────────────────────

export interface QualityReport {
  dark: boolean
  blurry: boolean
  small: boolean
}

/** Cheap checks on a downscaled copy. These only produce warnings; they never block reading. */
export function assessQuality(source: HTMLCanvasElement): QualityReport {
  const small = Math.max(source.width, source.height) < 800
  const scale = Math.min(1, 600 / Math.max(source.width, source.height))
  const canvas = newCanvas(source.width * scale, source.height * scale)
  const ctx = ctx2d(canvas)
  ctx.drawImage(source, 0, 0, canvas.width, canvas.height)
  const { width, height } = canvas
  const gray = luminance(ctx.getImageData(0, 0, width, height).data)

  let sum = 0
  for (const v of gray) sum += v
  const mean = sum / gray.length

  // Variance of the Laplacian: low values mean few sharp edges (blur)
  let lapSum = 0
  let lapSq = 0
  let n = 0
  for (let y = 1; y < height - 1; y += 1) {
    for (let x = 1; x < width - 1; x += 1) {
      const i = y * width + x
      const lap = gray[i - width] + gray[i + width] + gray[i - 1] + gray[i + 1] - 4 * gray[i]
      lapSum += lap
      lapSq += lap * lap
      n += 1
    }
  }
  const lapVar = n > 0 ? lapSq / n - (lapSum / n) ** 2 : 0

  return { dark: mean < 70, blurry: lapVar < 45, small }
}

// ── Preprocessing ─────────────────────────────────────────────────────────────

/**
 * Prepares a phone photo for OCR:
 * 1. scale so text is large enough (long edge ~2000-2400 px),
 * 2. grayscale,
 * 3. flatten uneven lighting/shadows by dividing by an estimate of the paper
 *    brightness (block maximum, smoothed) — this also suppresses paper texture noise,
 * 4. stretch contrast between the 1st and 99th percentiles.
 * Tesseract does its own binarisation and small-angle deskew afterwards.
 */
export function preprocessForOcr(source: HTMLCanvasElement, options: { flattenLighting?: boolean } = {}): HTMLCanvasElement {
  const longEdge = Math.max(source.width, source.height)
  const target = longEdge < 1600 ? Math.min(longEdge * 2, 2000) : Math.min(longEdge, 2400)
  const scale = target / longEdge
  const canvas = newCanvas(source.width * scale, source.height * scale)
  const ctx = ctx2d(canvas)
  ctx.imageSmoothingQuality = 'high'
  ctx.drawImage(source, 0, 0, canvas.width, canvas.height)

  const { width, height } = canvas
  const image = ctx.getImageData(0, 0, width, height)
  const gray = luminance(image.data)

  if (options.flattenLighting !== false) {
    const block = 32
    const bw = Math.ceil(width / block)
    const bh = Math.ceil(height / block)
    const bg = new Float32Array(bw * bh)
    for (let y = 0; y < height; y += 1) {
      const row = Math.floor(y / block) * bw
      for (let x = 0; x < width; x += 1) {
        const k = row + Math.floor(x / block)
        const v = gray[y * width + x]
        if (v > bg[k]) bg[k] = v
      }
    }
    // Smooth the block grid (3x3 mean) so stamps/borders don't create seams
    const smooth = new Float32Array(bg.length)
    for (let by = 0; by < bh; by += 1) {
      for (let bx = 0; bx < bw; bx += 1) {
        let total = 0
        let count = 0
        for (let dy = -1; dy <= 1; dy += 1) {
          for (let dx = -1; dx <= 1; dx += 1) {
            const yy = by + dy
            const xx = bx + dx
            if (yy >= 0 && yy < bh && xx >= 0 && xx < bw) {
              total += bg[yy * bw + xx]
              count += 1
            }
          }
        }
        smooth[by * bw + bx] = Math.max(total / count, 40)
      }
    }
    // Divide by bilinearly interpolated background
    for (let y = 0; y < height; y += 1) {
      const fy = Math.min(Math.max(y / block - 0.5, 0), bh - 1)
      const y0 = Math.floor(fy)
      const y1 = Math.min(y0 + 1, bh - 1)
      const ty = fy - y0
      for (let x = 0; x < width; x += 1) {
        const fx = Math.min(Math.max(x / block - 0.5, 0), bw - 1)
        const x0 = Math.floor(fx)
        const x1 = Math.min(x0 + 1, bw - 1)
        const tx = fx - x0
        const b =
          smooth[y0 * bw + x0] * (1 - tx) * (1 - ty) +
          smooth[y0 * bw + x1] * tx * (1 - ty) +
          smooth[y1 * bw + x0] * (1 - tx) * ty +
          smooth[y1 * bw + x1] * tx * ty
        const i = y * width + x
        gray[i] = Math.min(255, (gray[i] / b) * 245)
      }
    }
  }

  // Contrast stretch between 1st and 99th percentile
  const histogram = new Uint32Array(256)
  for (const v of gray) histogram[Math.round(v)] += 1
  const lowCount = gray.length * 0.01
  const highCount = gray.length * 0.99
  let low = 0
  let high = 255
  for (let v = 0, acc = 0; v < 256; v += 1) {
    acc += histogram[v]
    if (acc <= lowCount) low = v
    if (acc <= highCount) high = v
  }
  const range = Math.max(high - low, 40)
  const data = image.data
  for (let i = 0, j = 0; j < gray.length; i += 4, j += 1) {
    const v = Math.max(0, Math.min(255, ((gray[j] - low) / range) * 255))
    data[i] = v
    data[i + 1] = v
    data[i + 2] = v
    data[i + 3] = 255
  }
  ctx.putImageData(image, 0, 0)
  return canvas
}

// ── Tesseract worker (cached between documents for speed) ─────────────────────

type TesseractWorker = Awaited<ReturnType<typeof import('tesseract.js')['createWorker']>>
let cachedWorker: { key: string; worker: TesseractWorker } | null = null
let progressListener: ((p: OcrProgress) => void) | null = null

async function getWorker(models: string[]): Promise<TesseractWorker> {
  const key = [...models].sort().join('+')
  if (cachedWorker?.key === key) return cachedWorker.worker
  await releaseOcr()
  const { createWorker, OEM } = await import('tesseract.js')
  const worker = await createWorker(models, OEM.LSTM_ONLY, {
    logger: (m: { status: string; progress: number }) => {
      if (!progressListener) return
      if (m.status.includes('loading') || m.status.includes('language')) {
        progressListener({ stage: 'download', progress: m.progress })
      } else if (m.status === 'recognizing text') {
        progressListener({ stage: 'recognize', progress: m.progress })
      }
    },
    errorHandler: () => undefined,
    workerPath: `${TESSERACT_BASE}/worker.min.js`,
    corePath: `${TESSERACT_BASE}/core`,
    langPath: `${TESSERACT_BASE}/lang`,
    workerBlobURL: false
  })
  cachedWorker = { key, worker }
  return worker
}

/** Frees the OCR engine (and any image data it holds in memory). */
export async function releaseOcr(): Promise<void> {
  const current = cachedWorker
  cachedWorker = null
  if (current) await current.worker.terminate().catch(() => undefined)
}

interface TesseractLine { text: string; confidence: number }
interface TesseractBlock { paragraphs?: Array<{ lines?: TesseractLine[] }> }

async function recognizeCanvas(worker: TesseractWorker, canvas: HTMLCanvasElement, page: number): Promise<OcrLine[]> {
  const result = await worker.recognize(canvas, { rotateAuto: true }, { text: true, blocks: true })
  const blocks = (result.data.blocks ?? []) as TesseractBlock[]
  const lines: OcrLine[] = []
  for (const block of blocks) {
    for (const paragraph of block.paragraphs ?? []) {
      for (const line of paragraph.lines ?? []) {
        lines.push({ text: line.text.replace(/\n$/, ''), confidence: line.confidence, page })
      }
      lines.push({ text: '', confidence: 100, page }) // paragraph break
    }
  }
  if (lines.length === 0 && result.data.text.trim()) {
    for (const text of result.data.text.split('\n')) lines.push({ text, confidence: result.data.confidence, page })
  }
  return lines
}

function toResult(lines: OcrLine[], pages: number, source: OcrResult['source']): OcrResult {
  const textLines = lines.filter((l) => l.text.trim())
  const weight = textLines.reduce((acc, l) => acc + l.text.length, 0)
  const confidence = weight > 0 ? textLines.reduce((acc, l) => acc + l.confidence * l.text.length, 0) / weight : 0
  return { lines, text: lines.map((l) => l.text).join('\n'), confidence, pages, source }
}

/** OCR one or more prepared page images. */
export async function recognizePages(
  canvases: HTMLCanvasElement[],
  models: string[],
  onProgress?: (p: OcrProgress) => void
): Promise<OcrResult> {
  progressListener = onProgress ?? null
  try {
    const worker = await getWorker(models)
    const lines: OcrLine[] = []
    for (let i = 0; i < canvases.length; i += 1) {
      progressListener = (p) => onProgress?.({ ...p, page: i + 1, pages: canvases.length })
      lines.push(...(await recognizeCanvas(worker, canvases[i], i + 1)))
    }
    return toResult(lines, canvases.length, 'ocr')
  } finally {
    progressListener = null
  }
}

// ── PDF ───────────────────────────────────────────────────────────────────────

type PdfDocument = Awaited<ReturnType<typeof import('pdfjs-dist')['getDocument']>['promise']>

async function getPdfJs() {
  const pdfjs = await import('pdfjs-dist')
  if (!pdfjs.GlobalWorkerOptions.workerSrc) {
    pdfjs.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.mjs', import.meta.url).toString()
  }
  return pdfjs
}

export async function openPdf(file: File): Promise<PdfDocument> {
  const pdfjs = await getPdfJs()
  return pdfjs.getDocument({ data: new Uint8Array(await file.arrayBuffer()) }).promise
}

export async function renderPdfPage(pdf: PdfDocument, pageNumber: number, scale: number): Promise<HTMLCanvasElement> {
  const page = await pdf.getPage(pageNumber)
  const viewport = page.getViewport({ scale })
  const canvas = newCanvas(viewport.width, viewport.height)
  const ctx = ctx2d(canvas)
  ctx.fillStyle = '#fff'
  ctx.fillRect(0, 0, canvas.width, canvas.height)
  await page.render({ canvasContext: ctx, viewport }).promise
  return canvas
}

/** Reads the PDF's own text layer, grouped into lines by vertical position. */
export async function readPdfTextLayer(pdf: PdfDocument, pages: number[]): Promise<OcrResult> {
  const lines: OcrLine[] = []
  for (const pageNumber of pages) {
    const page = await pdf.getPage(pageNumber)
    const content = await page.getTextContent()
    const rows = new Map<number, Array<{ x: number; str: string }>>()
    for (const item of content.items) {
      if (!('str' in item) || !item.str.trim()) continue
      const y = Math.round(item.transform[5] / 3) * 3
      const row = rows.get(y) ?? []
      row.push({ x: item.transform[4], str: item.str })
      rows.set(y, row)
    }
    const ys = [...rows.keys()].sort((a, b) => b - a)
    let previousY: number | null = null
    for (const y of ys) {
      if (previousY !== null && previousY - y > 24) lines.push({ text: '', confidence: 100, page: pageNumber })
      const text = rows.get(y)!.sort((a, b) => a.x - b.x).map((r) => r.str).join(' ')
      lines.push({ text, confidence: 95, page: pageNumber })
      previousY = y
    }
    lines.push({ text: '', confidence: 100, page: pageNumber })
  }
  return toResult(lines, pages.length, 'pdf-text')
}

/**
 * Many Indian government PDFs embed text with legacy/broken font mappings
 * (e.g. "व द्यालय" for "विद्यालय", control characters, or Kruti Dev
 * "Latin" gibberish). Such a text layer is worse than OCR of the page image.
 */
export function looksLikeBrokenTextLayer(text: string): boolean {
  const indicTokens = text.split(/\s+/).filter((t) => /[\u0900-\u0DFF]/.test(t))
  const controlChars = (text.match(/[\u0000-\u0008\u000E-\u001F]/g) ?? []).length
  if (controlChars > 3) return true
  if (indicTokens.length >= 20) {
    const startsWithMark = indicTokens.filter((t) => /^\p{M}/u.test(t)).length / indicTokens.length
    const singleLetter = indicTokens.filter((t) => [...t].length === 1).length / indicTokens.length
    if (startsWithMark >= 0.02 || singleLetter >= 0.06) return true
  }
  // Kruti Dev and similar fonts: Hindi stored as Latin letters with ; Z [ ] inside words
  const latinTokens = text.split(/\s+/).filter((t) => /^[\x21-\x7E]{3,}$/.test(t) && /[a-z]/.test(t))
  if (latinTokens.length >= 20) {
    const odd = latinTokens.filter((t) => /[a-z][;Z\]\[{}~`|][a-z]/i.test(t) || /^[a-z]*[;Z][a-z]/.test(t)).length
    if (odd / latinTokens.length > 0.1) return true
  }
  return false
}

/** Reads selected PDF pages: the text layer when it is usable, otherwise OCR of the rendered pages. */
export async function readPdf(
  pdf: PdfDocument,
  pages: number[],
  models: string[],
  onProgress?: (p: OcrProgress) => void
): Promise<OcrResult> {
  const textLayer = await readPdfTextLayer(pdf, pages)
  const hasText = textLayer.text.replace(/\s/g, '').length > 40 * pages.length
  if (hasText && !looksLikeBrokenTextLayer(textLayer.text)) return textLayer
  const canvases: HTMLCanvasElement[] = []
  for (const pageNumber of pages) {
    const rendered = await renderPdfPage(pdf, pageNumber, 2)
    canvases.push(preprocessForOcr(rendered, { flattenLighting: false }))
  }
  return recognizePages(canvases, models, onProgress)
}
